import { useEffect, useRef } from "react";
// REDUX
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
// SOCKET
import { subscribeWebSocketMessage } from "../socketMessageState/webSocketMessageBus";
// INTERFACES
import {
  LayoutInterface,
  SocketMessageInterface,
  WithheldMark,
} from "../../../interfaces/Monitoring.interface";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { AckOutcome } from "../../../flatbuffers/generated/front-end/ack-outcome";
import { Control } from "../../../flatbuffers/generated/control/control";
// UTILS
import notifyActionOutcome from "../../../utils/actionOutcomeNotification";
import { ActionOutcome, describeOutcome } from "../../../utils/actionVerification";
import { uuidToString } from "../../../utils/uuid";
import { WITHHELD_MARK_TTL_MS, withheldMarkKey } from "../../../flatbuffers/decoders/withheldMarks";
import { BankFloorInterface } from "../../../interfaces/Bank.interface";
import { readFloorIndexField, readFloorNoField } from "../../../utils/bankFloorOpenings";

/** What a withheld-command notice names: where it was held, and what. */
export interface WithheldNoticeSubject {
  /** "Car 2 (Bank A)", or the lift id when the lift is not in any mounted layout. */
  liftLabel: string;
  /** The floor's name, or null when the command addressed no floor. */
  floorLabel: string | null;
  /** The withheld command, e.g. "SecureCarCall"; null when it cannot be named. */
  commandName: string | null;
}

/**
 * The heading and sentence a withheld-command notice shows.
 *
 * Says what did NOT happen and why, because the operator is looking at a floor
 * where their press appears to have vanished: the command was not sent, and it
 * was the interlock — not the link, not the button — that stopped it.
 */
export function withheldNoticeText(subject: WithheldNoticeSubject): {
  title: string;
  detail: string;
} {
  const what = subject.commandName ?? "A command";
  const where = subject.floorLabel
    ? `${subject.liftLabel}, floor ${subject.floorLabel}`
    : subject.liftLabel;
  return {
    title: describeOutcome(ActionOutcome.Withheld).title,
    detail:
      `${where}: ${what} was not sent. The interlock it waits on did not confirm in time, ` +
      "so the edge dropped the command instead of sending it. Try again once the device is ready.",
  };
}

/**
 * Name the lift, floor and command of a withheld verdict from the mounted
 * layouts, falling back to the raw ids when a layout does not know them.
 */
export function withheldNoticeSubject(
  layouts: readonly LayoutInterface[],
  bankId: string,
  liftId: string,
  mark: WithheldMark | null,
): WithheldNoticeSubject {
  const layout = layouts.find((l) => l.idBank === bankId);
  const lift = (layout?.lifts ?? []).find(
    (l) => uuidToString(l.id as UuidT | string | null) === liftId,
  );
  const liftName = typeof lift?.name === "string" && lift.name ? lift.name : liftId;
  const liftLabel = layout?.name ? `${liftName} (${layout.name})` : liftName;
  if (mark === null) return { liftLabel, floorLabel: null, commandName: null };
  // The grid's layouts carry the bank's floors as they arrived — snake_case
  // from the cascader JSON — so both casings are read.
  const floor = (layout?.bankFloors ?? []).find(
    (f) => Number(readFloorIndexField(f)) === mark.position,
  );
  return {
    liftLabel,
    floorLabel: floor ? floorTextLabel(floor) : String(mark.position),
    commandName: Control[mark.control] ?? `Control ${mark.control}`,
  };
}

/**
 * A floor as a sentence names it: its full name, or — for a floor the bank
 * never named — its own number, the fallback every floor button uses
 * (`floorButtonLabel`). Never its index: on a bank numbered from 1 the index is
 * one floor short, so a notice would name the floor below the one the grid
 * shows the car arriving at.
 */
function floorTextLabel(floor: BankFloorInterface): string {
  if (floor.name) return floor.name;
  return String(readFloorNoField(floor) ?? readFloorIndexField(floor));
}

/**
 * Tell the operator whenever VTS withholds a command, whichever control sent it.
 *
 * WHY A GLOBAL LISTENER. A channel modifier with `timeout_policy` DROP holds a
 * command until an interlock confirms, and drops it when the confirmation does
 * not come, answering with an `AckOutcome.Withheld` verdict. Action buttons hear
 * verdicts through `useActionOutcome`, but the control panel's car-call buttons
 * do not use it — and a car call is exactly what a car-call lock gates. So one
 * listener on the unsampled bus, mounted once beside the sockets, owns the
 * notice for every press; `useActionOutcome` settles its own press silently so
 * the same verdict is never announced twice.
 *
 * NOT gated on the `commandNotifications` preference, for the reason
 * `notifyCommandBlocked` gives: that setting silences routine chatter, and a
 * command that was never sent is not chatter. (`ActionOutcome.Withheld` is not
 * silenceable in `notifyActionOutcome` either.)
 *
 * ONE NOTICE PER VERDICT. Each Withheld verdict for an Elevator Control command
 * leaves a mark, keyed by bank, lift, floor and verdict time, and a mark is
 * announced once however many frames repeat it. A verdict that leaves no mark
 * (a command that addresses no floor) is announced without one — unless the
 * lift already carries marks, in which case the frame is taken to be a repeat
 * of the verdict that left them.
 */
export default function useWithheldCommandNotices(layouts: readonly LayoutInterface[] = []): void {
  const theme = useSelector((state: RootState) => state.home.theme);

  // Read through a ref so the subscription is registered ONCE: re-subscribing
  // when the layouts or theme change would drop verdicts in the gap.
  const latest = useRef({ layouts, theme });
  latest.current = { layouts, theme };

  useEffect(() => {
    // Mark key -> when it was announced (wall clock), forgotten after two TTLs:
    // by then the reducer has pruned the mark, so it cannot come round again.
    const announced = new Map<string, number>();

    const notify = (msg: SocketMessageInterface, mark: WithheldMark | null) => {
      const subject = withheldNoticeSubject(
        latest.current.layouts,
        uuidToString(msg.bankId),
        uuidToString(msg.liftId),
        mark,
      );
      const { title, detail } = withheldNoticeText(subject);
      notifyActionOutcome({
        outcome: ActionOutcome.Withheld,
        title,
        detail,
        theme: latest.current.theme,
      });
    };

    const unsubscribe = subscribeWebSocketMessage((msg) => {
      if (msg.ack?.outcome !== AckOutcome.Withheld) return;

      const now = Date.now();
      announced.forEach((at, key) => {
        if (now - at > 2 * WITHHELD_MARK_TTL_MS) announced.delete(key);
      });

      const bankId = uuidToString(msg.bankId);
      const liftId = uuidToString(msg.liftId);
      const marks = msg.withheldCalls ?? [];
      const fresh = marks.filter((mark) => !announced.has(withheldMarkKey(bankId, liftId, mark)));

      if (fresh.length === 0) {
        if (marks.length === 0) notify(msg, null);
        return;
      }
      fresh.forEach((mark) => {
        announced.set(withheldMarkKey(bankId, liftId, mark), now);
        notify(msg, mark);
      });
    });

    return unsubscribe;
  }, []);
}
